import { useState, useEffect, createContext } from "react";
import { getMainView, getViewerView, getSideViewerView, callApi } from "./Helpers";
import { useParams } from "react-router-dom";
import Viewer from "./components/Viewer";
import MainBody from "./components/MainBody";
import Backdrop from "./components/Backdrop";
import SideViewer from "./components/SideViewer";
import ConfirmDialog from "./components/ConfirmDialog";
import { ToastContainer, toast } from 'react-toastify';
import { MdCheckCircle, MdError, MdInfo, MdWarning } from "react-icons/md";
import SideBar from "./components/SideBar";
import TopBar from "./components/TopBar";
import MainMenuBtn from "./components/MainMenuBtn";
import Login from "./views/Login";
import OverlayLoader from "./components/OverlayLoader";
import MainLoader from "./components/MainLoader";
import { getAccessibleNavTarget } from "./accessControl";

export const AppContext = createContext(null);

const toastMeta = {
  success: {
    title: "Success",
    icon: MdCheckCircle,
  },
  error: {
    title: "Something went wrong",
    icon: MdError,
  },
  info: {
    title: "Heads up",
    icon: MdInfo,
  },
  warning: {
    title: "Check this",
    icon: MdWarning,
  },
};

function AppToast({ type, message }) {
  const meta = toastMeta[type] || toastMeta.info;
  const Icon = meta.icon;

  return (
    <div className={`AppToast AppToast--${type}`}>
      <div className="AppToast-glow"></div>
      <div className="AppToast-accentGrid"></div>
      <div className="AppToast-iconWrap">
        <div className="AppToast-iconHalo"></div>
        <Icon className="AppToast-icon" />
      </div>
      <div className="AppToast-copy">
        <div className="AppToast-meta">
          <span className="AppToast-badge">{type}</span>
          <span className="AppToast-time">now</span>
        </div>
        <div className="AppToast-title">{meta.title}</div>
        <div className="AppToast-message">{message}</div>
      </div>
    </div>
  );
}

export default function App(props) {

  const [ready, setReady] = useState(false); //for checking if app is ready

  const { _navItem, _navSubItem, _navExtraItem, _navMoreItem } = useParams(); //for routing purposes

  const [navItem, setNavItem] = useState(_navItem); //routing 
  const [navSubItem, setNavSubItem] = useState(_navSubItem); //routing
  const [navExtraItem, setNavExtraItem] = useState(_navExtraItem); //routing
  const [navMoreItem, setNavMoreItem] = useState(_navMoreItem); //routing

  const [showViewer, setShowViewer] = useState(false); //controlling the display of Viewer component
  const [viewerView, setViewerView] = useState(null); //the view to be shown in viewer

  const [showSideViewer, setShowSideViewer] = useState(false); //controlling the display of SideViewer component
  const [sideViewerView, setSideViewerView] = useState(null); //the view to be shown in viewer

  const [mainView, setMainView] = useState(null); //the view tobe shown in MainBody

  const [showOverlayLoader, setShowOverlayLoader] = useState(false); //controlling the display of OverlayLoader

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState("");

  const [isSideBarOpen, setIsSideBarOpen] = useState(false); //controls open and close for sidebar in mobile devices

  const [auth, setAuth] = useState(false); //track user authorization status

  //currentSession
  const [currentUserData, setCurrentUserData] = useState(null);

  //App level data
  const [systemParams, setSystemParams] = useState(null);
  const [allAdmins, setAllAdmins] = useState(null);
  const [allNews, setAllNews] = useState(null);
  const [allCustomers, setAllCustomers] = useState(null);
  const [allMotorcycles, setAllMotorcycles] = useState(null);
  const [allSales, setAllSales] = useState(null);
  const [allDebts, setAllDebts] = useState(null);
  const [allPayments, setAllPayments] = useState(null);
  const [allNewsCategories, setAllNewsCategories] = useState(null);
  const [allProjects, setAllProjects] = useState(null);
  const [allProjectCategories, setAllProjectCategories] = useState(null);
  const [allGallery, setAllGallery] = useState(null);
  const [allGalleryCategories, setAllGalleryCategories] = useState(null);
  const [allPartnershipRequests, setAllPartnershipRequests] = useState(null);
  

  async function init() {
    /**
     * Initialize the app here
     */

    setReady(false);
    await getSystemParams().then(async () => {
      await authCheck().then(async (_auth) => {
        if (_auth) {
          await getCurrentUserData();

        }
      })

      //turn off loaders after initialization
      setReady(true);
    })
  }

  async function getSystemParams() {
    return new Promise(async resolve => {
      await callApi('get-system-params', {}).then(response => {
        if (response.status === 1) {
          setSystemParams(response.data);
          resolve(response.data)
        } else {
          resolve(null)
        }
      })
    })
  }

  function navBack() {
    window.history.back();
    setShowOverlayLoader(false);
  }

  function showToast(type, msg) {
    toast(
      <AppToast type={type} message={msg} />,
      {
        className: `AppToast-shell AppToast-shell--${type}`,
        bodyClassName: "AppToast-body",
        progressClassName: `AppToast-progress AppToast-progress--${type}`,
        icon: false,
      }
    );
  }

  function tellError(msg) {
    showToast("error", msg);
  }

  function tellInfo(msg) {
    showToast("info", msg);
  }

  function tellWarning(msg) {
    showToast("warning", msg);
  }

  function tellMessage(msg) {
    showToast("success", msg);
  }

  function refresh() {
    /**
     * This function refreshes the whole app
     */
    window.location.reload(); //remember to optimize
  }

  function navTo(nav) {
    /**
     * This function handles navigation inside the app
     * Utilizing Hash based routing
     * nav is the object supporting the following keys: item, subItem, extraItem, moreItem
     */
    if (nav) {
      const safeNav = getAccessibleNavTarget(currentUserData, nav);
      //..

      let url = '';
      if (safeNav.item) {
        url = `#/${safeNav.item}/`
      }

      if (safeNav.subItem) {
        url += `${safeNav.subItem}/`
      }

      if (safeNav.extraItem) {
        url += `${safeNav.extraItem}/`
      }

      if (safeNav.moreItem) {
        url += `${safeNav.moreItem}/`
      }

      window.location.href = url;
      //..
    }
  }

  async function getCurrentUserData() {
    await callApi('get-current-user-data', {}).then(response => {
      if (response.status === 1) {
        setCurrentUserData(response.data);
      }
    })
  }

  function authCheck() {
    return new Promise(async resolve => {
      await callApi('auth-check', {}).then(response => {
        if (response.status === 1) {
          setAuth(true);
          resolve(true)
        } else {
          setAuth(false);
          resolve(false);
        }
      })
    })
  }

  function activateDialog(params) {
    let {
      message,
      onConfirm
    } = params;
    setConfirmDialogAction(() => { return onConfirm });
    setConfirmDialogMessage(message)
    setShowConfirmDialog(true);
  }

  async function getAllNews() {
    await callApi("get-all-news", {}).then(response => {
      if (response.status === 1) {
        setAllNews(response.data);
      }
    })
  }

  async function getAllAdmins() {
    await callApi("get-all-admins", {}).then(response => {
      if (response.status === 1) {
        setAllAdmins(response.data);
      }
    })
  }
  

  
  async function getAllCustomers() {
    await callApi("get-all-customers", {}).then(response => {
      if (response.status === 1) {
        setAllCustomers(response.data);
      }
    })
  }

  
  async function getAllMotorcycles() {
    await callApi("get-all-motorcycles", {}).then(response => {
      if (response.status === 1) {
        setAllMotorcycles(response.data);
      }
    })
  }

  async function getAllSales() {
    await callApi("get-all-sales", {}).then(response => {
      if (response.status === 1) {
        setAllSales(response.data);
      }
    })
  }

  
  async function getAllDebts() {
    await callApi("get-all-debts", {}).then(response => {
      if (response.status === 1) {
        setAllDebts(response.data);
      }
    })
  }

  async function getAllPayments() {
    await callApi("get-all-payments", {}).then(response => {
      if (response.status === 1) {
        setAllPayments(response.data);
      }
    })
  }


  async function getAllPartnershipRequests() {
    await callApi("get-all-partnership-requests", {}).then(response => {
      if (response.status === 1) {
        setAllPartnershipRequests(response.data);
      }
    })
  }

  async function getAllNewsCategories() {
    await callApi("get-all-news-categories", {}).then(response => {
      if (response.status === 1) {
        setAllNewsCategories(response.data);
      }
    })
  }

  async function getAllGallery() {
    await callApi("get-all-gallery", {}).then(response => {
      if (response.status === 1) {
        setAllGallery(response.data);
      }
    })
  }

  async function getAllProjects() {
    await callApi("get-all-projects", {}).then(response => {
      if (response.status === 1) {
        setAllProjects(response.data);
      }
    })
  }

  async function getAllProjectCategories() {
    await callApi("get-all-project-categories", {}).then(response => {
      if (response.status === 1) {
        setAllProjectCategories(response.data);
      }
    })
  }

  async function getAllGalleryCategories() {
    await callApi("get-all-gallery-categories", {}).then(response => {
      if (response.status === 1) {
        setAllGalleryCategories(response.data);
      }
    })
  }

  

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    if (isSideBarOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
  }, [isSideBarOpen])

  useEffect(() => {
    setNavItem(_navItem);
    setNavSubItem(_navSubItem);
    setNavExtraItem(_navExtraItem);
    setNavMoreItem(_navMoreItem);
  }, [_navItem, _navSubItem, _navExtraItem, _navMoreItem])

  useEffect(() => {
    if (currentUserData) {
      //handle current user data changes here
      const safeNav = getAccessibleNavTarget(currentUserData, {
        item: navItem || "dashboard",
        subItem: navSubItem,
        extraItem: navExtraItem,
        moreItem: navMoreItem,
      });

      if (
        safeNav.item !== navItem ||
        safeNav.subItem !== navSubItem ||
        safeNav.extraItem !== navExtraItem ||
        safeNav.moreItem !== navMoreItem
      ) {
        navTo(safeNav);
      }
    }
  }, [currentUserData, navItem, navSubItem, navExtraItem, navMoreItem])


  useEffect(() => {

    //check for viewers
    if (navItem === 'view') {
      //activate viewer
      setShowViewer(true);
      setViewerView(getViewerView(appContext));

      //hide other viewers
      setShowSideViewer(false)
      setSideViewerView(null);

    } else if (navItem === 'side-view') {
      //activate viewer
      setShowSideViewer(true);
      setSideViewerView(getSideViewerView(appContext));

      //hide other viewers
      setShowViewer(false)
      setViewerView(null);

    } else {
      //just set normal views
      setShowViewer(false);
      setShowSideViewer(false)

      setViewerView(null);
      setSideViewerView(null);

      const _mainView = getMainView(appContext)
      if (_mainView) {
        setMainView(_mainView);
      }
    }
  }, [navItem, navSubItem, navExtraItem, navMoreItem]);

  const appContext = {
    refresh,
    navTo,
    mainView,
    viewerView,
    setShowOverlayLoader,
    navItem,
    navSubItem,
    navExtraItem,
    navMoreItem,
    setShowViewer,
    showViewer,
    navBack,
    showSideViewer,
    setShowSideViewer,
    sideViewerView,
    activateDialog,
    setShowConfirmDialog,
    confirmDialogAction,
    confirmDialogMessage,
    showConfirmDialog,
    tellError,
    tellInfo,
    tellMessage,
    tellWarning,
    isSideBarOpen,
    setIsSideBarOpen,
    showOverlayLoader,
    auth,
    currentUserData,
    allNews,
    allNewsCategories,
    getAllNews,
    allCustomers,
    getAllCustomers,
    allDebts,
    getAllDebts,
    allMotorcycles,
    getAllMotorcycles,
    allSales,
    getAllSales,
    allPayments,
    getAllPayments,
    allAdmins,
    getAllAdmins
  }

  const toastContainer = (
    <ToastContainer
      position="bottom-right"
      autoClose={3200}
      hideProgressBar={false}
      closeButton={false}
      newestOnTop
      pauseOnFocusLoss
      pauseOnHover
      draggable
      draggableDirection="x"
      limit={4}
      toastClassName="AppToast-shell"
      bodyClassName="AppToast-body"
      progressClassName="AppToast-progress"
    />
  );

  if (ready) {
    if (auth) {
      if (currentUserData) {
        return (
          <AppContext.Provider value={appContext}>
            <MainBody />
            <TopBar />
            <MainMenuBtn />
            <SideBar />
            <Viewer />
            <SideViewer />
            <ConfirmDialog />
            <OverlayLoader />
            {toastContainer}
          </AppContext.Provider>
        )
      } else {
        return (
          <AppContext.Provider value={appContext}>
            {toastContainer}
            <MainLoader />
          </AppContext.Provider>
        )
      }
    } else {
      return (
        <AppContext.Provider value={appContext}>
          <OverlayLoader />
          {toastContainer}
          <Login />
        </AppContext.Provider>
      )
    }
  } else {
    return (
      <AppContext.Provider value={appContext}>
        {toastContainer}
        <MainLoader />
      </AppContext.Provider>
    )
  }
}
